import { useMemo, useState } from "react";
import type { BankAccount } from "@/hooks/use-bank-accounts";

import { useOpeningBalancesMatrixRange } from "@/hooks/finance/reconciliation/useStartingBalance";
import { useExpectedClosingMatrixRange } from "@/hooks/finance/reconciliation/useExpectedClosing";
import { useLedgerExpectedMatrixRange } from "@/hooks/finance/reconciliation/useLedgerExpected";
import { useLedgerGapMatrixRange } from "@/hooks/finance/reconciliation/useLedgerGap";
import { useTxInOutMatrixRange } from "@/hooks/finance/reconciliation/useBankTransactionsInAndOut";
import { useFeesMatrixRange } from "@/hooks/finance/reconciliation/useFees";
import { usePaidExpensesMatrixRange } from "@/hooks/finance/reconciliation/useExpenses";

import { GAP_EPS, METRICS, type MetricKey } from "./TableHelpers";

export function useReconciliationDailyMatrix(args: {
  accounts: BankAccount[];
  dateFrom: string;
  dateTo: string;
  selectedAccountId: string | null;
  onlyGaps: boolean;
}) {
  const { accounts, dateFrom, dateTo, selectedAccountId, onlyGaps } = args;

  const bankAccountIds = useMemo(() => accounts.map((a) => a.id), [accounts]);

  // идентично 1-й странице: gap по умолчанию включен и "locked"
  const [metrics, setMetrics] = useState<Record<MetricKey, boolean>>({
    opening: false,
    expected: false,
    ledgerExpected: false,
    gap: true,
    txIn: false,
    txOut: false,
    fees: false,
    exp: false,
  });

  const enabledMetrics = useMemo(() => {
    const list = METRICS.filter((m) => metrics[m.key]);
    const base = list.length === 0 ? METRICS.filter((m) => m.key === "gap") : list;

    // gap всегда последним
    return base.slice().sort((a, b) => {
      if (a.key === "gap") return 1;
      if (b.key === "gap") return -1;
      return 0;
    });
  }, [metrics]);

  const toggle = (key: MetricKey) => {
    setMetrics((prev) => {
      if (key === "gap" && prev.gap) return prev; // gap locked ON
      return { ...prev, [key]: !prev[key] };
    });
  };

  const openingEnabled = metrics.opening;
  const expectedEnabled = metrics.expected;
  const ledgerExpectedEnabled = metrics.ledgerExpected;
  const gapEnabled = metrics.gap;
  const txEnabled = metrics.txIn || metrics.txOut;
  const feesEnabled = metrics.fees;
  const expEnabled = metrics.exp;

  const openingQ = useOpeningBalancesMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: openingEnabled,
  } as any);

  const expectedQ = useExpectedClosingMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: expectedEnabled,
  } as any);

  const ledgerExpectedQ = useLedgerExpectedMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: ledgerExpectedEnabled,
  } as any);

  const ledgerGapQ = useLedgerGapMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: gapEnabled,
  } as any);

  const txInOutQ = useTxInOutMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: txEnabled,
  } as any);

  const feesQ = useFeesMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: feesEnabled,
  } as any);

  const paidExpensesQ = usePaidExpensesMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: expEnabled,
  } as any);

  const resetToGap = () => {
    setMetrics({
      opening: false,
      expected: false,
      ledgerExpected: false,
      gap: true,
      txIn: false,
      txOut: false,
      fees: false,
      exp: false,
    });
  };

  const selectAll = () => {
    setMetrics({
      opening: true,
      expected: true,
      ledgerExpected: true,
      gap: true,
      txIn: true,
      txOut: true,
      fees: true,
      exp: true,
    });
  };

  const isLoading =
    (openingEnabled && openingQ.isLoading) ||
    (expectedEnabled && expectedQ.isLoading) ||
    (ledgerExpectedEnabled && ledgerExpectedQ.isLoading) ||
    (gapEnabled && ledgerGapQ.isLoading) ||
    (txEnabled && txInOutQ.isLoading) ||
    (feesEnabled && feesQ.isLoading) ||
    (expEnabled && paidExpensesQ.isLoading);

  const isError =
    (openingEnabled && openingQ.isError) ||
    (expectedEnabled && expectedQ.isError) ||
    (ledgerExpectedEnabled && ledgerExpectedQ.isError) ||
    (gapEnabled && ledgerGapQ.isError) ||
    (txEnabled && txInOutQ.isError) ||
    (feesEnabled && feesQ.isError) ||
    (expEnabled && paidExpensesQ.isError);

  const primaryKey = enabledMetrics[0]?.key ?? "gap";

  // 1:1 с первой страницей: dateKeys берём из primary включенной метрики
  const dateKeys =
    (primaryKey === "gap" ? ledgerGapQ.data?.dateKeys : undefined) ??
    (primaryKey === "ledgerExpected" ? ledgerExpectedQ.data?.dateKeys : undefined) ??
    (primaryKey === "expected" ? expectedQ.data?.dateKeys : undefined) ??
    (primaryKey === "opening" ? openingQ.data?.dateKeys : undefined) ??
    ((primaryKey === "txIn" || primaryKey === "txOut") ? txInOutQ.data?.dateKeys : undefined) ??
    (primaryKey === "fees" ? feesQ.data?.dateKeys : undefined) ??
    (primaryKey === "exp" ? paidExpensesQ.data?.dateKeys : undefined) ??
    [];

  const getMapsForAccount = (accountId: string) => ({
    opening: openingEnabled ? openingQ.data?.byAccount.get(accountId) : undefined,
    expected: expectedEnabled ? expectedQ.data?.byAccount.get(accountId) : undefined,
    ledgerExpected: ledgerExpectedEnabled ? ledgerExpectedQ.data?.byAccount.get(accountId) : undefined,
    gap: gapEnabled ? ledgerGapQ.data?.byAccount.get(accountId) : undefined,
    txInOut: txEnabled ? txInOutQ.data?.byAccount.get(accountId) : undefined,
    fees: feesEnabled ? feesQ.data?.byAccount.get(accountId) : undefined,
    paidExpenses: expEnabled ? paidExpensesQ.data?.byAccount.get(accountId) : undefined,
  });

  const accountsToRender = useMemo(() => {
    let list = accounts;

    if (selectedAccountId) {
      list = list.filter((a) => a.id === selectedAccountId);
    }

    if (!onlyGaps) return list;

    // onlyGaps теперь строго по Ledger gap (как в 1-й таблице)
    return list.filter((acc) => {
      const maps = getMapsForAccount(acc.id);
      const gapMap = maps.gap;
      if (!gapMap || dateKeys.length === 0) return false;

      for (const d of dateKeys) {
        const cell = gapMap.get(d);
        const gap = (cell?.ledger_gap ?? null) as number | null;

        // если gap отсутствует (нет данных) — считаем "интересным" (как ты делал раньше)
        if (gap == null) return true;

        if (Math.abs(gap) > GAP_EPS) return true;
      }

      return false;
    });
  }, [accounts, selectedAccountId, onlyGaps, dateKeys, openingEnabled, expectedEnabled, ledgerExpectedEnabled, gapEnabled, txEnabled, feesEnabled, expEnabled]);

  return {
    metrics,
    enabledMetrics,
    toggle,
    resetToGap,
    selectAll,
    dateKeys,
    isLoading,
    isError,
    getMapsForAccount,
    accountsToRender,
  };
}